import type { Ref } from 'vue';
import { useRouter } from 'vue-router';

import type {
  IAffiliateProgramCreativeAffiliateAttributeFields,
  IAffiliateProgramCreativeFormFields,
} from '@modules/affiliate-program-creatives/submodules/edit/types';
import type { IApiFormErrors } from '@modules/core/services/query/types';
import { AFFILIATE_PROGRAM_CREATIVES_LIST_ROUTE_NAME } from '@modules/affiliate-program-creatives/constants';
import { useAffiliateProgramCreativeSaveMutation } from '@modules/affiliate-program-creatives/submodules/edit/mutations';
import { RESPONSE_DATE_FORMAT } from '@modules/smart-filters/constants';
import { formatDate } from '@vueuse/core';

export interface AffiliateProgramCreativeSaveComposable {
  clearError(error: string): void;
  errors: Ref<IApiFormErrors | undefined>;
  isLoadingSave: Ref<boolean>;
  onSubmit(values: IAffiliateProgramCreativeFormFields): Promise<void>;
}

export default function useAffiliateProgramCreativeSave(
  creativeItem: Ref<IAffiliateProgramCreativeFormFields | undefined>,
  isEdit: Ref<boolean>,
  selectedAffiliates: Ref<number[]>,
): AffiliateProgramCreativeSaveComposable {
  const router = useRouter();

  const {
    mutateAsync: saveCreative,
    isLoading: isLoadingSave,
    clearError,
    errors,
  } = useAffiliateProgramCreativeSaveMutation();

  async function onSubmit(values: IAffiliateProgramCreativeFormFields): Promise<void> {
    const { affiliate_ids, ...restValues } = values;

    const creative_exclusive_affiliates_attributes: IAffiliateProgramCreativeAffiliateAttributeFields[] = [];
    const allAffIds = [
      ...selectedAffiliates.value,
      ...((affiliate_ids ?? []).filter((item) => !selectedAffiliates.value.includes(item))),
    ];

    for (const affId of allAffIds) {
      const inOld = selectedAffiliates.value.includes(affId);
      const inNew = affiliate_ids?.includes(affId);

      if (!inOld && inNew) {
        creative_exclusive_affiliates_attributes.push({
          affiliate_id: affId,
        });
      } else if (inOld && !inNew) {
        const affiliateItem = creativeItem.value?.exclusive_affiliates?.find((item) => item.affiliate_id === affId);

        if (affiliateItem) {
          creative_exclusive_affiliates_attributes.push({
            ...affiliateItem,
            _destroy: 1,
          });
        }
      }
    }

    await saveCreative({
      ...(creativeItem.value ?? {}),
      ...restValues,
      at_beginning_show: formatDate(values.at_beginning_show, RESPONSE_DATE_FORMAT),
      at_end_show: formatDate(values.at_end_show, RESPONSE_DATE_FORMAT),
      creative_exclusive_affiliates_attributes,
    });

    if (!isEdit.value) {
      router.back({
        name: AFFILIATE_PROGRAM_CREATIVES_LIST_ROUTE_NAME,
        params: {
          type: restValues.format,
        },
      });
    }
  }

  return {
    clearError,
    errors,
    isLoadingSave,
    onSubmit,
  };
}
